import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { LoadingShimmerModule } from 'loading-shimmer';
import { MaskitoOptions } from '@maskito/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { AddressFormGroup } from '../../../forms/address-form-group';
import { DropDownItem } from '../../../models/shared/stylesheet/drop-down-item';
import { AddressFormService } from '../../../services/address-form.service';
import { GeographyService } from '../../../services/geography/geography.service';
import { TextInputComponent } from '../../form-components/text-input/text-input.component';
import { DropdownInputComponent } from '../../../views/shared/components/form-components/dropdown-input/dropdown-input.component';
import { FormErrorComponent } from '../../../views/shared/components/form-components/form-error/form-error.component';
import { MaskedTextInputComponent } from '../../../views/shared/components/form-components/masked-text-input/masked-text-input.component';
import { CANADA_COUNTRY_ID } from '../../../consts/consts';
import { CANADA_POSTAL_CODE_MASK_OPTIONS } from '../../../utils/canada-postal-code-mask';

@Component({
  selector: 'app-international-address-form-grid',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LoadingShimmerModule,
    TextInputComponent,
    DropdownInputComponent,
    FormErrorComponent,
    MaskedTextInputComponent
  ],
  templateUrl: './international-address-form-grid.component.html',
  styleUrls: ['./international-address-form-grid.component.scss'],
  providers: [AddressFormService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InternationalAddressFormGridComponent implements OnChanges {
  @Input() form!: AddressFormGroup;
  @Input() formSubmitted = false;
  @Input() controlIdPrefix = 'internationalAddress';

  @Input() set isLoading(isLoading: boolean) {
    this._isLoading.next(isLoading);
  }

  private geographyService = inject(GeographyService);
  private addressFormService = inject(AddressFormService);

  private _isLoading = new BehaviorSubject(false);

  public isLoading$ = combineLatest([
    this._isLoading.asObservable(),
    this.geographyService.countriesLoading$,
    this.geographyService.statesLoading$
  ]).pipe(map(loadingFlags => loadingFlags.some(Boolean)));

  public countryDropdowns$ = this.geographyService.countries$.pipe(
    map(countries => countries.map(country => new DropDownItem(country.name, country.id))),
    catchError(() => of([] as DropDownItem[]))
  );

  public stateDropdowns$: Observable<DropDownItem[]> = this.addressFormService.selectedCountryId$.pipe(
    switchMap(countryId =>
      this.geographyService.statesForCountry(countryId).pipe(
        map(states => states.map(state => new DropDownItem(state.name, state.id))),
        catchError(() => of([] as DropDownItem[]))
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** Countries with no states of their own hide the field outright rather than show an empty dropdown. */
  public stateFieldHidden$ = this.stateDropdowns$.pipe(map(stateDropdowns => stateDropdowns.length === 0));

  public stateAndPostalCodeOptional$ = this.addressFormService.stateAndPostalCodeOptional$;

  public postalCodeIsMasked$ = this.addressFormService.selectedCountryId$.pipe(
    map(countryId => countryId === CANADA_COUNTRY_ID)
  );

  public postalCodeMaskOptions: MaskitoOptions = CANADA_POSTAL_CODE_MASK_OPTIONS;

  public controlIds = InternationalAddressFormGridComponent.buildControlIds('internationalAddress');

  public labelShimmerHeight = '1.25rem';
  public inputShimmerHeight = '3.5rem';

  public customErrorMessages = {
    countryId: {
      required: $localize`Country is required`
    },
    addressLine1: {
      required: $localize`Address Line 1 is required`
    },
    city: {
      required: $localize`City/Town is required`
    },
    stateId: {
      required: $localize`Province/State/Region is required`
    },
    postalCode: {
      required: $localize`Postal Code/ZIP is required`,
      invalidPostalCode: $localize`Invalid postal code`,
      minlength: $localize`Invalid postal code`,
      maxlength: $localize`Invalid postal code`
    }
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.controlIdPrefix) {
      this.controlIds = InternationalAddressFormGridComponent.buildControlIds(this.controlIdPrefix);
    }

    if (changes.form && this.form) {
      this.addressFormService.manageInternationalAddressControls(
        this.form.controls.countryId,
        this.form.controls.stateId,
        this.form.controls.postalCode
      );
    }
  }

  private static buildControlIds(prefix: string): Record<keyof AddressFormGroup['controls'], string> {
    return {
      countryId: `${prefix}Country`,
      addressLine1: `${prefix}AddressLine1`,
      addressLine2: `${prefix}AddressLine2`,
      addressLine3: `${prefix}AddressLine3`,
      city: `${prefix}City`,
      stateId: `${prefix}State`,
      postalCode: `${prefix}PostalCode`
    };
  }
}
